import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

// ── Constants ──────────────────────────────────────────────────────────────

const TABS = [
  { key: 'info',     label: 'Business Info',  icon: '🏢' },
  { key: 'invoice',  label: 'Invoice & Tax',  icon: '🧾' },
  { key: 'smtp',     label: 'Email / SMTP',   icon: '📧' },
  { key: 'whatsapp', label: 'WhatsApp',       icon: '💬' },
  { key: 'ccavenue', label: 'CCAvenue',       icon: '💳' },
];

const PAYMENT_TERMS = ['Due on Receipt','Net 7','Net 15','Net 30','Net 45','Net 60','Net 90'];

const TIMEZONES = [
  'UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Toronto','America/Sao_Paulo','Europe/London','Europe/Paris','Europe/Berlin',
  'Europe/Moscow','Asia/Dubai','Asia/Kolkata','Asia/Bangkok','Asia/Singapore',
  'Asia/Tokyo','Asia/Shanghai','Australia/Sydney','Pacific/Auckland',
];

const CURRENCIES = [
  { code:'USD', sym:'$',   label:'US Dollar'         },
  { code:'EUR', sym:'€',   label:'Euro'              },
  { code:'GBP', sym:'£',   label:'British Pound'     },
  { code:'INR', sym:'₹',   label:'Indian Rupee'      },
  { code:'AED', sym:'د.إ', label:'UAE Dirham'        },
  { code:'SAR', sym:'﷼',   label:'Saudi Riyal'       },
  { code:'JPY', sym:'¥',   label:'Japanese Yen'      },
  { code:'CNY', sym:'¥',   label:'Chinese Yuan'      },
  { code:'AUD', sym:'A$',  label:'Australian Dollar' },
  { code:'CAD', sym:'C$',  label:'Canadian Dollar'   },
  { code:'CHF', sym:'CHF', label:'Swiss Franc'       },
  { code:'SGD', sym:'S$',  label:'Singapore Dollar'  },
];

const DATE_FORMATS = ['YYYY-MM-DD','DD/MM/YYYY','MM/DD/YYYY','DD-MM-YYYY','MMM DD, YYYY','DD MMM YYYY'];
const LANGUAGES    = [
  { code:'en', label:'English' }, { code:'ar', label:'Arabic'   },
  { code:'fr', label:'French'  }, { code:'es', label:'Spanish'  },
  { code:'de', label:'German'  }, { code:'hi', label:'Hindi'    },
  { code:'zh', label:'Chinese' }, { code:'ja', label:'Japanese' },
];

const BLANK = {
  name:'', tagline:'', business_email:'', phone:'', address:'', website:'', logo_url:'',
  gst_number:'', pan_number:'', reg_number:'', tax_rate:'',
  invoice_prefix:'', invoice_start_number:'', payment_terms:'',
  bank_name:'', bank_account_number:'', bank_ifsc:'', bank_branch:'',
  invoice_terms:'', invoice_notes:'',
  smtp_host:'', smtp_port:587, smtp_user:'', smtp_password:'',
  smtp_from_email:'', smtp_from_name:'', smtp_encryption:'tls',
  wa_api_key:'', wa_phone_number_id:'', wa_business_account_id:'',
  wa_webhook_token:'', wa_api_url:'https://graph.facebook.com/v18.0',
  cc_enabled:false, cc_merchant_id:'', cc_access_code:'', cc_working_key:'', cc_environment:'test',
  timezone:'', currency:'', currency_symbol:'', date_format:'', language:'',
};

// ── Main Component ──────────────────────────────────────────────────────────

export default function Business() {
  const [businesses, setBusinesses] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);   // 'form' | 'delete'
  const [selected,   setSelected]   = useState(null);   // null = add mode
  const [tab,        setTab]        = useState('info');
  const [form,       setForm]       = useState(BLANK);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [toast,      setToast]      = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/businesses'); setBusinesses(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const openAdd    = ()  => { setForm(BLANK); setSelected(null); setTab('info'); setModal('form'); };
  const openEdit   = (b) => { setForm({ ...BLANK, ...b }); setSelected(b); setTab('info'); setModal('form'); };
  const openDelete = (b) => { setSelected(b); setModal('delete'); };
  const close      = ()  => { setModal(null); setSelected(null); };

  const save = async () => {
    if (!form.name?.trim()) return showToast('error', 'Business name is required.');
    setSaving(true);
    try {
      if (selected) await api.put(`/api/businesses/${selected.id}`, form);
      else           await api.post('/api/businesses', form);
      await load();
      showToast('success', selected ? 'Business updated.' : 'Business created.');
      close();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const del = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/businesses/${selected.id}`);
      await load();
      showToast('success', 'Business deleted.');
      close();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const setDefault = async (b) => {
    try {
      await api.post(`/api/businesses/${b.id}/set-default`);
      await load();
      showToast('success', `"${b.name}" set as default.`);
    } catch (e) {
      showToast('error', 'Could not update default.');
    }
  };

  const testSmtp = async () => {
    if (!selected) return;
    setTesting(true);
    try {
      const r = await api.post(`/api/businesses/${selected.id}/test-smtp`);
      showToast('success', r.data.message);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'SMTP test failed.');
    } finally { setTesting(false); }
  };

  const [waTestTo, setWaTestTo] = useState('');
  const testWhatsapp = async () => {
    if (!selected) { showToast('error', 'Save the business first, then send a test.'); return; }
    if (!waTestTo.trim()) { showToast('error', 'Enter a recipient number (with country code).'); return; }
    setTesting(true);
    try {
      const r = await api.post(`/api/businesses/${selected.id}/test-whatsapp`, { to_number: waTestTo.trim() });
      showToast('success', r.data.message);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'WhatsApp test failed.');
    } finally { setTesting(false); }
  };

  const validateCcavenue = async () => {
    if (!selected) { showToast('error', 'Save the business first, then validate.'); return; }
    setTesting(true);
    try {
      const r = await api.post(`/api/businesses/${selected.id}/validate-ccavenue`);
      showToast('success', r.data.message);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'CCAvenue validation failed.');
    } finally { setTesting(false); }
  };

  const onCurrencyChange = (code) => {
    const c = CURRENCIES.find(x => x.code === code);
    setForm(f => ({ ...f, currency: code, currency_symbol: c?.sym || '' }));
  };

  const defBiz = businesses.find(b => b.is_default);

  return (
    <Layout title="Business Profiles">

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 3000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px',
          fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* ── Summary bar ── */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Businesses', val: businesses.length,   dot: C.primary,  text: false },
          { label: 'Default',          val: defBiz?.name || '—', dot: C.success,  text: true  },
        ].map(({ label, val, dot, text }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '14px 22px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: text ? '14px' : '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add Business
        </button>
      </div>

      {/* ── Card grid / empty state ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: C.textSub }}>Loading...</div>
      ) : businesses.length === 0 ? (
        <div style={{
          background: C.white, borderRadius: '14px',
          border: `2px dashed ${C.border}`, padding: '60px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '14px' }}>🏢</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>
            No Business Added Yet
          </div>
          <div style={{ fontSize: '13px', color: C.textSub, marginBottom: '20px' }}>
            Add your first business profile to get started.
          </div>
          <button onClick={openAdd} style={btnPrimary}>+ Add Business</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {businesses.map(b => (
            <BusinessCard
              key={b.id} biz={b}
              onEdit={() => openEdit(b)}
              onDelete={() => openDelete(b)}
              onSetDefault={() => setDefault(b)}
              isOnly={businesses.length === 1}
            />
          ))}
        </div>
      )}

      {/* ── Form Modal (Add / Edit) ── */}
      {modal === 'form' && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '16px',
            width: '100%', maxWidth: '760px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
            border: `1px solid ${C.border}`,
          }}>

            {/* Modal header */}
            <div style={{
              padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
                <span style={{ fontSize: '16px', fontWeight: 700, color: C.textMain }}>
                  {selected ? `Edit — ${selected.name}` : 'Add New Business'}
                </span>
              </div>
              <button onClick={close} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            {/* Tab bar */}
            <div style={{
              display: 'flex', gap: '2px', padding: '12px 28px 0',
              borderBottom: `1px solid ${C.border}`, flexShrink: 0, overflowX: 'auto',
            }}>
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '8px 14px', border: 'none', cursor: 'pointer',
                  background: 'none', fontSize: '12px',
                  fontWeight: tab === t.key ? 700 : 500,
                  color: tab === t.key ? C.primary : C.textSub,
                  borderBottom: tab === t.key ? `2.5px solid ${C.primary}` : '2.5px solid transparent',
                  whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '5px',
                  transition: 'all 0.15s', marginBottom: '-1px',
                }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Tab content — scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

              {/* Business Info */}
              {tab === 'info' && (
                <>
                  <div style={grid2}>
                    <Field label="Business Name *">
                      <FInput value={form.name} onChange={v => set('name', v)} placeholder="Hive Technologies Ltd." />
                    </Field>
                    <Field label="Tagline">
                      <FInput value={form.tagline} onChange={v => set('tagline', v)} placeholder="Powering your enterprise" />
                    </Field>
                    <Field label="Business Email">
                      <FInput type="email" value={form.business_email} onChange={v => set('business_email', v)} placeholder="info@company.com" />
                    </Field>
                    <Field label="Phone">
                      <FInput type="tel" value={form.phone} onChange={v => set('phone', v)} placeholder="+1 555 000 0000" />
                    </Field>
                    <Field label="Website">
                      <FInput value={form.website} onChange={v => set('website', v)} placeholder="https://company.com" />
                    </Field>
                    <Field label="Logo URL">
                      <FInput value={form.logo_url} onChange={v => set('logo_url', v)} placeholder="https://..." />
                    </Field>
                  </div>
                  <Field label="Address">
                    <textarea value={form.address} onChange={e => set('address', e.target.value)}
                      placeholder="123 Business Street, City, Country" rows={3}
                      style={{ ...inputBase, resize: 'vertical', lineHeight: 1.5 }} />
                  </Field>
                </>
              )}

              {/* Invoice & Tax */}
              {tab === 'invoice' && (
                <>
                  <SectionDivider label="Tax & Registration Numbers" />
                  <div style={grid3}>
                    <Field label="GST / VAT Number">
                      <FInput value={form.gst_number} onChange={v => set('gst_number', v)} placeholder="22AAAAA0000A1Z5" />
                    </Field>
                    <Field label="PAN / Tax ID">
                      <FInput value={form.pan_number} onChange={v => set('pan_number', v)} placeholder="AAAAA0000A" />
                    </Field>
                    <Field label="Company Reg. No.">
                      <FInput value={form.reg_number} onChange={v => set('reg_number', v)} placeholder="CIN / Corp No." />
                    </Field>
                  </div>

                  <SectionDivider label="Invoice Configuration" />
                  <div style={grid3}>
                    <Field label="Invoice Prefix">
                      <FInput value={form.invoice_prefix} onChange={v => set('invoice_prefix', v)} placeholder="INV" />
                    </Field>
                    <Field label="Start From No.">
                      <FInput type="number" value={form.invoice_start_number} onChange={v => set('invoice_start_number', Number(v))} placeholder="1001" />
                    </Field>
                    <Field label="Tax Rate (%)">
                      <FInput type="number" value={form.tax_rate} onChange={v => set('tax_rate', Number(v))} placeholder="18" />
                    </Field>
                  </div>
                  <Field label="Default Payment Terms">
                    <FSelect value={form.payment_terms} onChange={v => set('payment_terms', v)}>
                      {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                    </FSelect>
                  </Field>

                  <SectionDivider label="Bank Account Details" />
                  <div style={grid2}>
                    <Field label="Bank Name">
                      <FInput value={form.bank_name} onChange={v => set('bank_name', v)} placeholder="HDFC Bank" />
                    </Field>
                    <Field label="Account Number">
                      <FInput value={form.bank_account_number} onChange={v => set('bank_account_number', v)} placeholder="0000 0000 0000 0000" />
                    </Field>
                    <Field label="IFSC / Routing / SWIFT">
                      <FInput value={form.bank_ifsc} onChange={v => set('bank_ifsc', v)} placeholder="HDFC0001234" />
                    </Field>
                    <Field label="Branch">
                      <FInput value={form.bank_branch} onChange={v => set('bank_branch', v)} placeholder="Main Branch" />
                    </Field>
                  </div>

                  <SectionDivider label="Invoice Footer Content" />
                  <Field label="Terms & Conditions">
                    <textarea value={form.invoice_terms} onChange={e => set('invoice_terms', e.target.value)}
                      placeholder="Payment due within 30 days. Late payments attract 1.5% interest per month..."
                      rows={3} style={{ ...inputBase, resize: 'vertical', lineHeight: 1.5 }} />
                  </Field>
                  <Field label="Footer Notes">
                    <textarea value={form.invoice_notes} onChange={e => set('invoice_notes', e.target.value)}
                      placeholder="Thank you for your business. Contact support@company.com for queries."
                      rows={2} style={{ ...inputBase, resize: 'vertical', lineHeight: 1.5 }} />
                  </Field>
                </>
              )}

              {/* Email / SMTP */}
              {tab === 'smtp' && (
                <>
                  <div style={grid2}>
                    <Field label="SMTP Host">
                      <FInput value={form.smtp_host} onChange={v => set('smtp_host', v)} placeholder="smtp.gmail.com" />
                    </Field>
                    <Field label="Port">
                      <FInput type="number" value={form.smtp_port} onChange={v => set('smtp_port', Number(v))} placeholder="587" />
                    </Field>
                    <Field label="Encryption">
                      <FSelect value={form.smtp_encryption} onChange={v => set('smtp_encryption', v)}>
                        <option value="tls">TLS (STARTTLS) — port 587</option>
                        <option value="ssl">SSL — port 465</option>
                        <option value="none">None — port 25</option>
                      </FSelect>
                    </Field>
                    <Field label="SMTP Username">
                      <FInput value={form.smtp_user} onChange={v => set('smtp_user', v)} placeholder="user@gmail.com" />
                    </Field>
                    <Field label="SMTP Password">
                      <SecretInput value={form.smtp_password} onChange={v => set('smtp_password', v)} placeholder="App password" />
                    </Field>
                    <Field label="From Name">
                      <FInput value={form.smtp_from_name} onChange={v => set('smtp_from_name', v)} placeholder="Hive ERP" />
                    </Field>
                  </div>
                  <Field label="From Email Address">
                    <FInput type="email" value={form.smtp_from_email} onChange={v => set('smtp_from_email', v)} placeholder="noreply@company.com" />
                  </Field>
                  <InfoBox text="Gmail: smtp.gmail.com · port 587 · TLS. Use an App Password (not your Google account password)." />
                  {selected ? (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                      <button onClick={testSmtp} disabled={testing} style={btnOutline}>
                        {testing ? 'Testing...' : '⚡ Test Connection'}
                      </button>
                    </div>
                  ) : (
                    <InfoBox text="Save the business first, then re-open to test the SMTP connection." />
                  )}
                </>
              )}

              {/* WhatsApp */}
              {tab === 'whatsapp' && (
                <>
                  <InfoBox text="Meta (WhatsApp Cloud API) credentials are required to push messages to clients directly. Get them from Meta for Developers → WhatsApp → API Setup. Use a permanent System User token for production." />

                  <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, margin: '4px 0 10px' }}>Meta Credentials (required to send)</div>
                  <Field label="Meta API Access Token (permanent) *">
                    <SecretInput value={form.wa_api_key} onChange={v => set('wa_api_key', v)} placeholder="EAAxxxxxxxxxxxxx..." />
                  </Field>
                  <div style={grid2}>
                    <Field label="Phone Number ID *">
                      <FInput value={form.wa_phone_number_id} onChange={v => set('wa_phone_number_id', v)} placeholder="123456789012345" />
                    </Field>
                    <Field label="WhatsApp Business Account ID">
                      <FInput value={form.wa_business_account_id} onChange={v => set('wa_business_account_id', v)} placeholder="987654321098765" />
                    </Field>
                    <Field label="Webhook Verify Token">
                      <FInput value={form.wa_webhook_token} onChange={v => set('wa_webhook_token', v)} placeholder="your_verify_token" />
                    </Field>
                    <Field label="Graph API Base URL">
                      <FInput value={form.wa_api_url} onChange={v => set('wa_api_url', v)} placeholder="https://graph.facebook.com/v18.0" />
                    </Field>
                  </div>

                  {(!form.wa_api_key || !form.wa_phone_number_id) && (
                    <div style={{ background: C.warningBg, color: C.warningText, border: '1px solid #FDE68A', borderRadius: '8px', padding: '9px 13px', fontSize: '12px', margin: '2px 0 14px' }}>
                      ⚠ Both the <strong>Meta API Access Token</strong> and <strong>Phone Number ID</strong> are needed before you can send WhatsApp messages to clients.
                    </div>
                  )}

                  {/* Test send */}
                  <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: '14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '8px' }}>Send a test message</div>
                    {!selected ? (
                      <div style={{ fontSize: '12px', color: C.textSub }}>Save this business first, then you can send a test WhatsApp message.</div>
                    ) : (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '220px' }}>
                          <FInput value={waTestTo} onChange={setWaTestTo} placeholder="Recipient number e.g. 919876543210" />
                        </div>
                        <button onClick={testWhatsapp} disabled={testing} style={{ ...btnPrimary, whiteSpace: 'nowrap' }}>
                          {testing ? 'Sending…' : '💬 Send Test'}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {tab === 'ccavenue' && (
                <>
                  <InfoBox text="CCAvenue lets clients pay invoices & renewals online. Get these from your CCAvenue dashboard → Settings → API Keys. The Working Key is your AES secret — keep it safe." />

                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: form.cc_enabled ? C.successBg : '#F9FAFB',
                    border: `1px solid ${form.cc_enabled ? '#6EE7B7' : C.border}`,
                    borderRadius: '10px', padding: '12px 16px', marginBottom: '18px',
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>Enable CCAvenue payments</div>
                      <div style={{ fontSize: '11px', color: C.textSub }}>Turn on to accept online payments for this business.</div>
                    </div>
                    <button type="button" onClick={() => set('cc_enabled', !form.cc_enabled)} style={{
                      width: '46px', height: '26px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                      background: form.cc_enabled ? C.success : '#D1D5DB', position: 'relative', transition: 'background 0.2s',
                    }}>
                      <span style={{
                        position: 'absolute', top: '3px', left: form.cc_enabled ? '23px' : '3px',
                        width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                      }} />
                    </button>
                  </div>

                  <div style={grid2}>
                    <Field label="Merchant ID *">
                      <FInput value={form.cc_merchant_id} onChange={v => set('cc_merchant_id', v)} placeholder="e.g. 123456" />
                    </Field>
                    <Field label="Environment">
                      <FSelect value={form.cc_environment} onChange={v => set('cc_environment', v)}>
                        <option value="test">Test / Staging</option>
                        <option value="production">Production / Live</option>
                      </FSelect>
                    </Field>
                  </div>
                  <Field label="Access Code *">
                    <SecretInput value={form.cc_access_code} onChange={v => set('cc_access_code', v)} placeholder="AVXXXXXXXXXXXXXXXXXX" />
                  </Field>
                  <Field label="Working Key (AES secret) *">
                    <SecretInput value={form.cc_working_key} onChange={v => set('cc_working_key', v)} placeholder="32-character working key" />
                  </Field>

                  <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: '14px', marginTop: '4px' }}>
                    {!selected ? (
                      <div style={{ fontSize: '12px', color: C.textSub }}>Save this business first, then you can validate the credentials.</div>
                    ) : (
                      <button onClick={validateCcavenue} disabled={testing} style={btnGhost}>
                        {testing ? 'Checking…' : '✓ Validate Credentials'}
                      </button>
                    )}
                  </div>
                </>
              )}

            </div>

            {/* Modal footer */}
            <div style={{
              padding: '16px 28px', borderTop: `1px solid ${C.border}`,
              display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0,
            }}>
              <button onClick={close} style={btnGhost}>Cancel</button>
              <button onClick={save} disabled={saving} style={btnPrimary}>
                {saving ? 'Saving...' : selected ? '💾 Update Business' : '+ Create Business'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Delete Confirm Modal ── */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{
            background: C.white, borderRadius: '14px', padding: '28px',
            width: '100%', maxWidth: '420px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.textMain, marginBottom: '12px' }}>
              Delete Business
            </div>
            <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: '0 0 20px' }}>
              Are you sure you want to delete{' '}
              <strong style={{ color: C.danger }}>{selected?.name}</strong>?<br />
              <span style={{ fontSize: '12px', color: C.textSub }}>This action cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={close} style={btnGhost}>Cancel</button>
              <button onClick={del} disabled={saving} style={btnDanger}>
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Business Card ─────────────────────────────────────────────────────────────

function BusinessCard({ biz, onEdit, onDelete, onSetDefault, isOnly }) {
  const initials = (biz.name || '?')[0].toUpperCase();
  return (
    <div style={{
      background: C.white, borderRadius: '14px',
      border: `1.5px solid ${biz.is_default ? C.primary : C.border}`,
      boxShadow: biz.is_default ? `0 0 0 3px ${C.primaryLight}` : '0 1px 4px rgba(0,0,0,0.05)',
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>

      {/* Card header */}
      <div style={{
        background: biz.is_default
          ? `linear-gradient(135deg, ${C.sidebar} 0%, #2A1A05 100%)`
          : '#F9FAFB',
        padding: '18px 18px 14px',
        display: 'flex', alignItems: 'flex-start', gap: '12px',
      }}>
        {biz.logo_url ? (
          <img src={biz.logo_url} alt=""
            style={{ width: '44px', height: '44px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0, border: `2px solid ${C.primary}` }} />
        ) : (
          <div style={{
            width: '44px', height: '44px', borderRadius: '8px', flexShrink: 0,
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '18px', color: '#fff',
          }}>{initials}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px', fontWeight: 700, color: biz.is_default ? '#fff' : C.textMain,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {biz.name}
          </div>
          {biz.tagline && (
            <div style={{
              fontSize: '11px', color: biz.is_default ? 'rgba(255,255,255,0.45)' : C.textSub,
              marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {biz.tagline}
            </div>
          )}
          {biz.is_default && (
            <span style={{
              display: 'inline-block', marginTop: '6px',
              background: 'rgba(245,166,35,0.2)', color: C.primary,
              border: `1px solid ${C.primary}50`, borderRadius: '20px',
              padding: '2px 10px', fontSize: '10px', fontWeight: 700,
            }}>⭐ Default</span>
          )}
        </div>
      </div>

      {/* Detail rows */}
      <div style={{ padding: '14px 18px', flex: 1 }}>
        {[
          { icon: '🏛', label: 'GST',      val: biz.gst_number      },
          { icon: '📄', label: 'PAN',      val: biz.pan_number      },
          { icon: '📞', label: 'Phone',    val: biz.phone           },
          { icon: '✉',  label: 'Email',    val: biz.business_email  },
          { icon: '🌐', label: 'Web',      val: biz.website         },
          { icon: '💱', label: 'Currency', val: biz.currency && biz.currency_symbol ? `${biz.currency} (${biz.currency_symbol})` : null },
          { icon: '🧾', label: 'Invoice',  val: (biz.invoice_prefix || biz.tax_rate > 0) ? `${biz.invoice_prefix || 'INV'}-XXXX · Tax ${biz.tax_rate ?? 0}%` : null },
          { icon: '🏦', label: 'Bank',     val: biz.bank_name       },
        ].filter(x => x.val).map(({ icon, label, val }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '5px' }}>
            <span style={{ fontSize: '11px', flexShrink: 0, width: '16px' }}>{icon}</span>
            <span style={{ color: C.textSub, fontSize: '10px', fontWeight: 600, minWidth: '54px', flexShrink: 0 }}>{label}:</span>
            <span style={{ fontSize: '12px', color: C.textMain, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
          </div>
        ))}
      </div>

      {/* Action row */}
      <div style={{
        padding: '12px 18px', borderTop: `1px solid ${C.border}`,
        display: 'flex', gap: '6px', justifyContent: 'flex-end',
      }}>
        {!biz.is_default && (
          <button onClick={onSetDefault} style={{ ...cardBtn, color: C.warning, background: `${C.warning}15`, border: `1px solid ${C.warning}30` }}>
            ⭐ Set Default
          </button>
        )}
        <button onClick={onEdit} style={{ ...cardBtn, color: '#3B82F6', background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          Edit
        </button>
        {!isOnly && (
          <button onClick={onDelete} style={{ ...cardBtn, color: C.danger, background: C.dangerBg, border: `1px solid #FECACA` }}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function SectionDivider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0 14px' }}>
      <span style={{ fontSize: '10px', fontWeight: 700, color: C.primaryDeep, textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: '1px', background: C.border }} />
    </div>
  );
}

function InfoBox({ text }) {
  return (
    <div style={{
      background: C.infoBg, color: C.infoText, border: `1px solid #BFDBFE`,
      borderRadius: '8px', padding: '10px 14px', margin: '0 0 16px',
      fontSize: '12px', lineHeight: 1.5, display: 'flex', gap: '8px',
    }}>
      <span style={{ flexShrink: 0 }}>ℹ</span> {text}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: '20px',
    }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FInput({ type = 'text', value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <input type={type} value={value ?? ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...inputBase, borderColor: focus ? C.primary : C.border }} />
  );
}

function FSelect({ value, onChange, children }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      style={{ ...inputBase, background: C.white, cursor: 'pointer', borderColor: C.border }}>
      {children}
    </select>
  );
}

function SecretInput({ value, onChange, placeholder }) {
  const [show,  setShow]  = useState(false);
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value ?? ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ ...inputBase, borderColor: focus ? C.primary : C.border, paddingRight: '42px' }} />
      <button type="button" onClick={() => setShow(s => !s)} style={{
        position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: C.textSub,
      }}>
        {show ? '🙈' : '👁'}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputBase = {
  width: '100%', padding: '9px 12px',
  border: '1.5px solid', borderRadius: '8px',
  fontSize: '13px', color: C.textMain,
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
};
const grid2  = { display: 'grid', gridTemplateColumns: '1fr 1fr',     gap: '0 18px' };
const grid3  = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 14px' };
const cardBtn = {
  padding: '5px 12px', borderRadius: '6px', fontSize: '11px',
  fontWeight: 600, cursor: 'pointer',
};
const btnPrimary = {
  padding: '10px 22px',
  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost = {
  padding: '10px 18px', background: C.primaryBg,
  color: C.textSub, border: `1px solid ${C.border}`,
  borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnDanger = {
  padding: '10px 20px', background: C.danger,
  color: '#fff', border: 'none', borderRadius: '8px',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
};
const btnOutline = {
  padding: '9px 16px', background: C.primaryBg, color: C.primaryDeep,
  border: `1.5px solid ${C.primary}`, borderRadius: '8px',
  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
};
